import type { CountryItem } from '@/types/countries'
import { useCallback, useMemo, useState } from 'react'

export interface FilteredCountries {
  pinned: CountryItem[]
  unpinned: CountryItem[]
}

export interface UseCountrySelectionReturn {
  tempSelected: number[]
  checked: boolean | 'indeterminate'
  searchQuery: string
  setSearchQuery: (query: string) => void
  filteredCountries: FilteredCountries
  handleToggle: (id: number) => void
  handleToggleAll: () => void
  isValid: boolean
  resetToCurrentSelection: (currentIds: number[]) => void
  applySelection: (setSearchParams: (updater: (prev: URLSearchParams) => URLSearchParams) => void) => void
}

export function useCountrySelection(
  currentSelectedIds: number[],
  countries: CountryItem[],
): UseCountrySelectionReturn {
  const allCountryIds = useMemo(
    () => countries.map(country => country.id),
    [countries],
  )

  const [tempSelected, setTempSelected] = useState<number[]>(
    currentSelectedIds.length > 0 ? currentSelectedIds : allCountryIds,
  )
  const [searchQuery, setSearchQuery] = useState('')

  const filteredCountries = useMemo<FilteredCountries>(() => {
    const query = searchQuery.trim().toLowerCase()

    const matching = query.length > 0
      ? countries.filter(c => c.name.toLowerCase().startsWith(query))
      : countries

    const pinned = matching
      .filter(c => tempSelected.includes(c.id))
      .sort((a, b) => a.name.localeCompare(b.name))

    const unpinned = matching
      .filter(c => !tempSelected.includes(c.id))
      .sort((a, b) => a.name.localeCompare(b.name))

    return { pinned, unpinned }
  }, [countries, tempSelected, searchQuery])

  const handleToggle = useCallback((id: number) => {
    setTempSelected(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id],
    )
  }, [])

  const checked = useMemo(() => {
    if (allCountryIds.length === 0)
      return false
    if (allCountryIds.every(id => tempSelected.includes(id)))
      return true
    if (tempSelected.length > 0)
      return 'indeterminate'
    return false
  }, [allCountryIds, tempSelected])

  const handleToggleAll = useCallback(() => {
    setTempSelected(prev =>
      allCountryIds.length > 0 && allCountryIds.every(id => prev.includes(id)) ? [] : allCountryIds,
    )
  }, [allCountryIds])

  const isValid = tempSelected.length > 0

  const resetToCurrentSelection = useCallback((currentIds: number[]) => {
    setTempSelected(currentIds.length > 0 ? currentIds : allCountryIds)
    setSearchQuery('')
  }, [allCountryIds])

  const applySelection = useCallback((
    setSearchParams: (updater: (prev: URLSearchParams) => URLSearchParams) => void,
  ) => {
    const isAllSelected = allCountryIds.length > 0
      && allCountryIds.every(id => tempSelected.includes(id))

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('country')
      next.delete('homologation_countries')
      if (!isAllSelected) {
        countries
          .filter(country => tempSelected.includes(country.id))
          .forEach(country => next.append('homologation_countries', country.code.toUpperCase()))
      }
      return next
    })
  }, [allCountryIds, countries, tempSelected])

  return {
    tempSelected,
    checked,
    searchQuery,
    setSearchQuery,
    filteredCountries,
    handleToggle,
    handleToggleAll,
    isValid,
    resetToCurrentSelection,
    applySelection,
  }
}
